// src/pages/income/env/disinfection/IncomeEnvDisinfectionNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from "./IncomeDraftCreate.module.css";
import api from "../../../../api";
import { useTabActivated } from "../../../../tabs/TabActivation";
import InvoiceForm from "./InvoiceForm";  // 청구서 
import StatementForm from "./StatementForm"; // 거래명세서
import RibbonTabs from "../../../../components/income/RibbonTabs";

const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };
const CODE_TO_PAY_METHOD = { transfer: "계좌이체", card: "카드결제", cash: "현금결제" };

export default function IncomeDraftCreate({ fixedCategory = "환경/소독" }) {
  const [activeTab, setActiveTab] = useState("draft");
  const [category] = useState(fixedCategory);
  const [subcategory, setSubcategory] = useState("소독방역");
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState(() => toISO(new Date())); 
  const [workMonth, setWorkMonth] = useState(""); 
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");
  const [seq, setSeq] = useState("001");
  const [loadingSeq, setLoadingSeq] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  // ✅ 거래처 관련 상태 — income.Partner 원장 자동완성(부분일치)
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // ✅ 환경/소독 청구서 발행 대상 거래처(선택 시 금액 자동 채움) + 저장 직후 결과 표시
  const [billingClient, setBillingClient] = useState(null);
  const [invoiceInfo, setInvoiceInfo] = useState(null);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");

  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);

  const draftNo = useMemo(
    () => `금천일자리(주)환경소독-${yymmdd}-${seq}호`,
    [yymmdd, seq]
  );

  // ✅ 작성일 변경 시 청구일 자동 설정
  useEffect(() => {
    setClaimDate(draftDate);
  }, [draftDate]);


  // ✅ seq 자동 생성
  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    async function getSeq() {
      if (!category || !draftDate) return;
      setLoadingSeq(true);
      try {
        const safeCategory = encodeURIComponent(category);
        const { data } = await api.get(`/income-drafts/next-seq/?category=${safeCategory}&date=${draftDate}`);
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (e) {
        console.error("[next-seq] failed:", e?.response?.status);
      } finally {
        if (!ignore) setLoadingSeq(false);
      }
    }
    getSeq();
    return () => { ignore = true; };
  }, [category, draftDate, seqRefreshTick]);

  // ✅ 거래처 원장(Partner) 부분일치 검색 — "올고운" 입력 시 "구립올고운어린이집" 표시
  useEffect(() => {
    let ignore = false;
    const q = vendorInput.trim();
    if (!q || vendorId) {
      setFilteredVendors([]);
      return;
    }
    setLoadingVendors(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get("partners/", { params: { q } });
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        const collator = new Intl.Collator("ko");
        data.sort((a, b) => collator.compare(a.name, b.name));
        if (!ignore) setFilteredVendors(data);
      } catch (err) {
        console.error("❌ 거래처 검색 실패:", err);
      } finally {
        if (!ignore) setLoadingVendors(false);
      }
    }, 200);
    return () => { ignore = true; clearTimeout(t); };
  }, [vendorInput, vendorId]);

  // ✅ 거래처 입력 시
  const handleChangeVendor = (e) => {
    setVendorInput(e.target.value);
    setVendorId(null);
    setBillingClient(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택 — 청구서 발행 대상(DisinfectionClient)이면 금액 자동 채움(수정 가능)
  const handlePickVendor = async (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
    setBillingClient(null);
    try {
      const res = await api.get("disinfection/clients/", { params: { partner: v.id, is_active: true } });
      const found = Array.isArray(res.data) ? res.data[0] : null;
      if (found) {
        setBillingClient(found);
        setAmount(String(found.amount));
      }
    } catch (err) {
      console.error("❌ 발행 거래처 확인 실패:", err);
    }
  };

  // ✅ 방향키 이동 및 Enter 선택
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 자동 제목 생성
  useEffect(() => {
    if (vendorInput && subcategory && workMonth) {
      const [year, month] = workMonth.split("-");

      // ✅ 거래처명에서 괄호 안 내용 제거 (예: "공원녹지과(모래소독)" → "공원녹지과")
      const cleanVendor = vendorInput.replace(/\(.*?\)/g, "").trim();

      // ✅ 제목에는 괄호 내용 제거된 거래처명 사용
      setTitle(`${year}년 ${month}월 ${cleanVendor} ${subcategory} 청구서`);

          }
  }, [vendorInput, subcategory, workMonth]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    // ✅ 유효성 검사 — 거래처는 목록 선택 없이 자유 입력도 허용(그 경우 청구서 미생성)
    if (!amount || !vendorInput) {
      alert("⚠️ 거래처와 금액을 입력해주세요.");
      setSaving(false);
      return;
    }

    if (!claimDate || !workMonth) {
      alert("⚠️ 청구일과 작업월을 입력해주세요.");
      setSaving(false);
      return;
    }

    const payload = {
      category,
      subcategory,
      title,
      draft_date: draftDate,
      claim_date: claimDate || null,
      work_month: workMonth,
      partner_id: vendorId || null,
      partner_name: vendorInput || "",
      amount: amount ? Number(amount) : 0,
      pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
      desc: desc || "",
      seq,
      draft_no: draftNo,
    };

    console.log("📤 전송 데이터:", payload);  // ✅ 디버깅용

    try {
      const { data } = await api.post(`/income-drafts/`, payload);
      setSavedId(data.id);
      setInvoiceInfo(data.disinfection_invoice_info || null);
      alert(`✅ 저장 완료!\n거래처: ${vendorInput}\n금액: ${amount}원`);
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);

      // ✅ 백엔드 에러 메시지 표시
      const errorMsg = err.response?.data?.detail
        || JSON.stringify(err.response?.data)
        || "서버 저장 실패";
      alert(`❌ ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  // ✅ 청구서 PDF 보기(인증 필요 — blob으로 받아 새 탭에서 연다)
  const viewInvoicePdf = async () => {
    if (!invoiceInfo) return;
    try {
      const res = await api.get(`disinfection/invoices/${invoiceInfo.id}/download/`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) {
      alert("PDF를 여는 데 실패했습니다: " + (err?.response?.data?.error || err.message));
    }
  };

  // ✅ 내보내기 기능
  const download = (fmt) => {
    if (!savedId) return alert("먼저 저장해 주세요.");
    window.open(`${api.defaults.baseURL}income-drafts/${savedId}/export?fmt=${fmt}`, "_blank");
  };

  return (
    <div className={styles.wrap}>

      <div className={styles.pane}>
        {activeTab === "draft" && (
          <form className={styles.form} onSubmit={onSubmit}>
            <h3 style={{ marginBottom: "10px" }}>환경/소독 수입데이터 입력</h3>

            <Row label="기안번호(자동완성)">
              <input value={draftNo} readOnly />
            </Row>

            <Row label="제목(자동완성)">
            <input
              value={title}
              readOnly
              placeholder="청구일·작업월·거래처 입력 시 자동 생성됩니다."
            />
            </Row>

            <Row label="사업구분"><input value={category} readOnly /></Row>

            <Row label="세부구분">
              <input value={subcategory} readOnly />
            </Row>

            <Row label="작성일">
              <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} />
            </Row>

            <Row label="청구일">
              <input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} />
            </Row>

            <Row label="작업월(귀속월)">
              <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} />
            </Row>

            <Row label="거래처">
              <div className={styles.partnerRow}>
                <input
                  placeholder="직접 입력 또는 검색"
                  value={vendorInput}
                  onChange={handleChangeVendor}
                  onKeyDown={handleVendorKeyDown}
                />
                <button
                  type="button"
                  className={styles.partnerButton}
                  onClick={() => setShowVendorList((v) => !v)}
                >
                  ▼
                </button>
              </div>
              {showVendorList && (
                <div className={styles.partnerBox}>
                  {loadingVendors ? (
                    <div>불러오는 중...</div>
                  ) : filteredVendors.length > 0 ? (
                    filteredVendors.map((v, i) => (
                      <div
                        key={v.id}
                        className={`${styles.partnerItem} ${i === highlightIndex ? styles.activeItem : ""}`}
                        onClick={() => handlePickVendor(v)}
                      >
                        {v.name}
                      </div>
                    ))
                  ) : (
                    <div>일치하는 거래처가 없습니다.</div>
                  )}
                </div>
              )}
              {billingClient && (
                <div style={{ color: "#2563eb", fontSize: "13px", marginTop: "4px", whiteSpace: "nowrap" }}>
                  ✓ 청구서 발행 거래처 — 저장 시 청구서가 자동 생성됩니다.
                </div>
              )}
            </Row>

            <Row label="금액">
              <input
                inputMode="numeric"
                placeholder="숫자만"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
              />
            </Row>

            <Row label="거래방법">
              <div className={styles.inline}>
                {PAY_METHOD_LABELS.map((label) => (
                  <label key={label} className={styles.check}>
                    <input
                      type="checkbox"
                      checked={payMethodLabel === label}
                      onChange={() => setPayMethodLabel(label)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </Row>

            <Row label="본문/비고">
              <textarea
                rows={4}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="청구 관련 메모를 입력하세요."
              />
            </Row>

            <div className={styles.actions}>
              <button type="submit" disabled={saving} className={styles.primaryBtn}>
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>

            {invoiceInfo && (
              <div className={styles.actions} style={{ color: "#16a34a", fontWeight: 600 }}>
                청구서 {invoiceInfo.round_no}회차 생성{" "}
                <button type="button" onClick={viewInvoicePdf} className={styles.primaryBtn} style={{ marginLeft: "8px" }}>
                  PDF 보기
                </button>
              </div>
            )}

            <div className={styles.actions}>
              <button type="button" onClick={() => download("docx")} className={styles.primaryBtn}>한글(DOCX)</button>
              <button type="button" onClick={() => download("pdf")} className={styles.primaryBtn}>PDF</button>
            </div>
          </form>
        )}
        {activeTab === "invoice" && <InvoiceForm />}     
        {activeTab === "statement" && <StatementForm />}       
      </div>
    </div>
  );
}

// ✅ 공통 Row
function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

// ✅ 날짜 포맷
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
